import { randomUUID } from 'node:crypto'
import { calculateDistance } from '@my-roadtrip/utils'
import type { AIModelTier } from '@my-roadtrip/types'
import { createLogger } from '../../common/utils/logger.js'
import { OpenAIService } from '../../services/openai.service.js'
import { MapboxPlacesService, type PlaceVerificationResult as MapboxVerificationResult } from '../../services/mapbox-places.service.js'
import { GooglePlacesService, type PlaceVerificationResult as GoogleVerificationResult } from '../../services/google-places.service.js'
import { OverpassService, type PlaceVerificationResult as OverpassVerificationResult } from '../../services/overpass.service.js'
import { AIRepository } from './ai.repository.js'
import { POIDatabaseService } from './poi-database.service.js'
import { SuggestPOIsRequestSchema, type SuggestPOIsRequest } from './ai.schemas.js'

const logger = createLogger('AIProcessor')
const SUGGESTION_TIMEOUT_MS = 4500
const VERIFICATION_TIMEOUT_MS = 4000
const MAX_VERIFICATION_CANDIDATES = 60
const MAX_VERIFICATION_CONCURRENCY = 8
const MAX_SUGGESTION_CONCURRENCY = 6
const MAX_CATEGORY_REQUESTS = 12
const MAX_QUICK_RESULTS = 60
const MIN_RESULTS_BEFORE_SKIPPING_AI = 18

const MIN_POI_CONFIDENCE = resolveNumberEnv('MIN_POI_CONFIDENCE', 60, 1, 100)
const MIN_DATABASE_RESULTS = resolveNumberEnv('MIN_DATABASE_RESULTS', 15, 1, 1000)
const ENABLE_POI_DATABASE = resolveBooleanEnv('ENABLE_POI_DATABASE', true)
const ALLOW_FIRESTORE_STUB = !['production', 'prod'].includes((process.env.NODE_ENV || '').toLowerCase())
const JOB_INPUT_SCHEMA = SuggestPOIsRequestSchema.pick({
  location: true,
  categories: true,
  radiusKm: true,
  preferences: true,
  context: true,
  planId: true,
  stageId: true,
  planName: true,
  stageName: true
})

type SuggestionPreferences = SuggestPOIsRequest['preferences']
type RouteContext = SuggestPOIsRequest['context']

// Default service instances (can be overridden for testing)
let openai = new OpenAIService()
let mapboxPlaces = new MapboxPlacesService()
let googlePlaces = new GooglePlacesService()
let overpassService = new OverpassService()
let repository = new AIRepository()

const resolveAdminFirestore = async () => {
  try {
    // Import firebase module and wait for initialization
    const { getFirestore, initializeFirebase } = await import('../../core/database/firebase.js')
    
    // Ensure Firebase is initialized (idempotent - safe to call multiple times)
    await initializeFirebase()
    
    // Now get the Firestore instance
    return getFirestore()
  } catch (error) {
    logger.warn('Failed to resolve Firestore', { error })
    if (!ALLOW_FIRESTORE_STUB) {
      throw error
    }
  }
  if (!ALLOW_FIRESTORE_STUB) {
    throw new Error('Firestore unavailable and stubbing disabled for this environment')
  }
  // Minimal stub to keep tests/unit flows alive when firestore is mocked or unavailable
  const noopCollection = () => ({
    where: () => noopCollection(),
    limit: () => noopCollection(),
    doc: () => ({ set: async () => undefined, get: async () => ({ exists: false }), update: async () => undefined }),
    add: async () => undefined,
    get: async () => ({ docs: [] })
  })
  return {
    collection: noopCollection
  } as any
}

// Lazy initialization of POI database
let poiDatabase: POIDatabaseService | null = null
let poiDatabaseInitPromise: Promise<void> | null = null

async function initPOIDatabase() {
  if (poiDatabase || !ENABLE_POI_DATABASE) return
  
  if (!poiDatabaseInitPromise) {
    poiDatabaseInitPromise = (async () => {
      const firestore = await resolveAdminFirestore()
      poiDatabase = new POIDatabaseService(firestore)
    })()
  }
  
  await poiDatabaseInitPromise
}

function getPOIDatabase(): POIDatabaseService | null {
  return poiDatabase
}

function resolveNumberEnv(name: string, defaultValue: number, min: number, max: number) {
  const raw = process.env[name]
  const parsed = raw ? Number.parseInt(raw, 10) : NaN
  if (Number.isFinite(parsed)) {
    return clampNumber(parsed, min, max)
  }
  return defaultValue
}

function resolveBooleanEnv(name: string, defaultValue: boolean) {
  const raw = process.env[name]
  if (raw === undefined) return defaultValue
  const normalized = raw.trim().toLowerCase()
  if (['false', '0', 'no', 'off'].includes(normalized)) return false
  if (['true', '1', 'yes', 'on'].includes(normalized)) return true
  return defaultValue
}

// Service injection for testing
export function injectServices(services: {
  openai?: OpenAIService
  mapbox?: MapboxPlacesService
  google?: GooglePlacesService
  overpass?: OverpassService
  repository?: AIRepository
  poiDatabase?: POIDatabaseService
}) {
  if (services.openai) openai = services.openai
  if (services.mapbox) mapboxPlaces = services.mapbox
  if (services.google) googlePlaces = services.google
  if (services.overpass) overpassService = services.overpass
  if (services.repository) repository = services.repository
  if (services.poiDatabase) poiDatabase = services.poiDatabase
}

export interface POIJobPayload {
  userId: string
  userTier?: 'free' | 'standard' | 'premium' | 'pro'
  location: { lat: number; lng: number }
  categories: string[]
  radiusKm: number
  preferences?: SuggestionPreferences
  context?: RouteContext
  cacheKey?: string
  planId?: string
  stageId?: string
  planName?: string
  stageName?: string
}

interface POIProcessingResult {
  success: boolean
  pois: any[]
  source: string
  metadata: Record<string, any>
  planId?: string
  stageId?: string
  planName?: string
  stageName?: string
}

function buildMetadata(
  source: string,
  processingTimeMs: number,
  radiusKm: number,
  categories: string[],
  cached: boolean,
  diagnostic?: {
    reason?: string
    aiReturnedCount?: number
    afterDedup?: number
    withinRadius?: number
    afterVerification?: number
    suggestions?: string[]
    // Technical admin fields
    timings?: {
      aiCallMs?: number
      verificationMs?: number
      totalMs?: number
    }
    services?: {
      openai?: { success: boolean; error?: string }
      google?: { success: boolean; verified: number; error?: string }
      mapbox?: { success: boolean; verified: number; error?: string }
    }
    filters?: {
      duplicatesRemoved?: number
      outsideRadius?: number
      failedVerification?: number
    }
    cacheHit?: boolean
    coordinates?: { lat: number; lng: number }
    rawError?: string
  }
) {
  const normalizedSource = (() => {
    if (source.startsWith('openai')) return 'openai'
    if (source.startsWith('mapbox-places')) return 'mapbox-places'
    if (source.startsWith('database')) return 'database'
    return source
  })()

  return {
    source: normalizedSource,
    processingTimeMs,
    generatedAt: new Date().toISOString(),
    model: source,
    radiusKm,
    categoryCount: categories?.length || 0,
    cached,
    ...(diagnostic && { diagnostic })
  }
}

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.min(Math.max(value, min), max)
}

function getDynamicConfidenceThreshold(radiusKm: number): number {
  if (radiusKm > 20) return 35
  if (radiusKm > 10) return 45
  return MIN_POI_CONFIDENCE
}

function createLimiter(limit: number) {
  let active = 0
  const queue: Array<() => void> = []

  const next = () => {
    if (active >= limit) return
    const task = queue.shift()
    if (task) task()
  }

  return async function run<T>(fn: () => Promise<T>): Promise<T> {
    if (active >= limit) {
      await new Promise<void>(resolve => queue.push(resolve))
    }
    active++
    try {
      return await fn()
    } finally {
      active--
      next()
    }
  }
}

function formatQuickPoi(
  poi: {
    name: string
    description: string
    location: { coordinates: { lat: number; lng: number } }
    category: string
    tags: string[]
    uniqueness: number
    rating?: number
    confidence?: number
    relevance?: number
    coverImage?: string
  },
  anchor: { lat: number; lng: number },
  radiusKm: number
) {
  const poiLat = poi.location.coordinates.lat
  const poiLng = poi.location.coordinates.lng
  const isInvalid = !Number.isFinite(poiLat) || !Number.isFinite(poiLng) || Math.abs(poiLat) > 90 || Math.abs(poiLng) > 180
  const isNearZeroZero = Math.abs(poiLat) < 0.01 && Math.abs(poiLng) < 0.01
  if (isInvalid || isNearZeroZero) {
    logger.warn('Invalid POI coordinates detected - skipping (quick)', {
      name: poi.name,
      lat: poiLat,
      lng: poiLng
    })
    return null
  }

  const distanceKm = calculateDistance(anchor, poi.location.coordinates) / 1000
  const distanceScore = clampNumber(100 - (distanceKm / Math.max(radiusKm, 1)) * 60, 0, 100)
  const uniqueness = clampNumber(poi.uniqueness ?? 50, 0, 100)
  const ratingScore = clampNumber((poi.rating ?? 0) * 20, 0, 100) // 5*20 = 100
  const aiConfidence = clampNumber(poi.confidence ?? 50, 0, 100)
  const tags = Array.isArray(poi.tags) ? poi.tags : []

  const confidence = clampNumber(
    uniqueness * 0.35 + distanceScore * 0.35 + ratingScore * 0.2 + aiConfidence * 0.1,
    0,
    100
  )
  const relevance = Math.round(confidence * 0.5 + uniqueness * 0.3 + distanceScore * 0.2)

  return {
    id: randomUUID(),
    name: poi.name,
    lat: poiLat,
    lng: poiLng,
    category: poi.category,
    description: poi.description,
    rating: poi.rating,
    uniqueness,
    distanceKm,
    tags,
    relevance,
    confidence,
    coverImage: poi.coverImage,
    verification: {
      source: 'unverified',
      matched: false,
      confidence,
      distanceMeters: distanceKm * 1000,
      name: poi.name,
      coordinates: poi.location.coordinates
    }
  }
}

function dedupePois<T extends { name: string; location: { coordinates: { lat: number; lng: number } } }>(pois: T[]): T[] {
  const seen = new Map<string, T>()
  for (const poi of pois) {
    const key = `${poi.name.trim().toLowerCase()}-${poi.location.coordinates.lat.toFixed(4)}-${poi.location.coordinates.lng.toFixed(4)}`
    if (!seen.has(key)) {
      seen.set(key, poi)
    }
  }
  return Array.from(seen.values())
}

/**
 * Get POI suggestions from Overpass (OpenStreetMap)
 * FREE tier source - unlimited, no cost
 */
async function getSuggestionsFromOverpass(
  location: { lat: number; lng: number },
  categories: string[],
  radiusKm: number
): Promise<Array<{
  name: string
  description: string
  location: { coordinates: { lat: number; lng: number } }
  category: string
  tags: string[]
  uniqueness: number
  rating?: number
  confidence?: number
  relevance?: number
}>> {
  const allPois: Array<{
    name: string
    description: string
    location: { coordinates: { lat: number; lng: number } }
    category: string
    tags: string[]
    uniqueness: number
    rating?: number
    confidence?: number
    relevance?: number
  }> = []

  // Search for each category
  for (const category of categories) {
    try {
      const results = await overpassService.searchNearby(
        location,
        [category],
        radiusKm * 1000
      )

      const transformedResults = results
        .filter(result => {
          // Filter out 0,0 coordinates from Overpass at source
          const lat = result.location.coordinates.lat
          const lng = result.location.coordinates.lng
          const isNearZeroZero = Math.abs(lat) < 0.01 && Math.abs(lng) < 0.01
          
          if (isNearZeroZero) {
            logger.warn('Overpass returned 0,0 coordinates - filtering', {
              name: result.name,
              lat,
              lng,
              category: result.category
            })
            return false
          }
          return true
        })
        .map(result => ({
          name: result.name,
          description: result.description,
          location: result.location,
          category: result.category,
          tags: result.tags || [],
          uniqueness: result.uniqueness || 50,
          rating: result.rating
        }))

      allPois.push(...transformedResults)
    } catch (error) {
      logger.warn('Overpass search failed for category', { category, error })
    }
  }

  return dedupePois(allPois)
}

/**
 * Tier-based source selection strategy
 * Optimizes costs by using appropriate sources per user tier
 */
function selectSourcesForTier(userTier: 'free' | 'standard' | 'premium' | 'pro' = 'standard'): {
  suggestionSources: Array<'overpass' | 'openai' | 'mapbox'>
  verificationSources: Array<'overpass' | 'google' | 'mapbox'>
} {
  switch (userTier) {
    case 'free':
      // FREE: No paid API calls - Overpass only
      return {
        suggestionSources: ['overpass'],
        verificationSources: ['overpass']
      }
    
    case 'standard':
      // STANDARD: Low-cost sources only (no OpenAI)
      return {
        suggestionSources: ['overpass', 'mapbox'],
        verificationSources: ['mapbox', 'overpass']
      }
    
    case 'premium':
      // PREMIUM: AI suggestions + triple verification + Mapbox secondary
      return {
        suggestionSources: ['openai', 'mapbox', 'overpass'],
        verificationSources: ['google', 'mapbox', 'overpass']
      }
    
    case 'pro':
      // PRO: Multi-source suggestions + full verification
      return {
        suggestionSources: ['openai', 'overpass', 'mapbox'],
        verificationSources: ['google', 'mapbox', 'overpass']
      }
    
    default:
      return {
        suggestionSources: ['overpass'],
        verificationSources: ['overpass']
      }
  }
}

/**
 * Verify POIs using tier-appropriate sources
 * Optimizes costs by only using paid APIs when tier allows
 */
async function verifyAndScorePoisWithTier(
  pois: Array<{
    name: string
    description: string
    location: { coordinates: { lat: number; lng: number } }
    category: string
    tags: string[]
    uniqueness: number
    rating?: number
    confidence?: number
    relevance?: number
  }>,
  anchor: { lat: number; lng: number },
  radiusKm: number,
  verificationSources: Array<'google' | 'mapbox' | 'overpass'>
) {
  const effectiveRadiusKm = Math.max(radiusKm, 1)
  const maxDistanceKm = effectiveRadiusKm
  const uniquePois = dedupePois(pois)
  
  const filteredPois = uniquePois.filter(poi => {
    const distanceKm = calculateDistance(anchor, poi.location.coordinates) / 1000
    if (distanceKm > maxDistanceKm) {
      logger.debug(`Rejecting POI "${poi.name}" - too far (${distanceKm.toFixed(1)}km > ${maxDistanceKm}km)`)
      return false
    }
    return true
  })

  const verificationPool = rankAndLimitPois(filteredPois, anchor, MAX_VERIFICATION_CANDIDATES)
  const verifyLimiter = createLimiter(MAX_VERIFICATION_CONCURRENCY)

  logger.info('Verifying POIs with tier-based verification', {
    total: pois.length,
    unique: uniquePois.length,
    withinRadius: filteredPois.length,
    verificationPool: verificationPool.length,
    limit: MAX_VERIFICATION_CANDIDATES,
    radiusKm: maxDistanceKm,
    sources: verificationSources
  })

  // Verify each POI with tier-appropriate sources (limited pool to protect external APIs)
  const allVerifications = await Promise.all(
    verificationPool.map(async (poi) => {
      const verifications: {
        google?: GoogleVerificationResult
        mapbox?: MapboxVerificationResult
        overpass?: OverpassVerificationResult
      } = {}

      // Only call services that are in the tier's verification list
      const promises: Array<Promise<void>> = []

      if (verificationSources.includes('google')) {
        promises.push(
          verifyLimiter(() =>
            withTimeout(
              googlePlaces.verifyPlace(poi.name, poi.location.coordinates, {
                proximity: anchor,
                maxDistanceMeters: maxDistanceKm * 1000
              }),
              VERIFICATION_TIMEOUT_MS,
              'Google verification',
              {
                matched: false,
                confidence: 0,
                source: 'google-places' as const,
                distanceMeters: undefined,
                name: undefined,
                location: undefined
              }
            )
          ).then(result => { verifications.google = result })
        )
      }

      if (verificationSources.includes('mapbox')) {
        promises.push(
          verifyLimiter(() =>
            withTimeout(
              mapboxPlaces.verifyPlace(poi.name, poi.location.coordinates, {
                proximity: anchor,
                maxDistanceMeters: maxDistanceKm * 1000
              }),
              VERIFICATION_TIMEOUT_MS,
              'Mapbox verification',
              {
                matched: false,
                confidence: 0,
                source: 'mapbox' as const,
                distanceMeters: undefined,
                name: undefined,
                location: undefined
              }
            )
          ).then(result => { verifications.mapbox = result })
        )
      }

      if (verificationSources.includes('overpass')) {
        promises.push(
          verifyLimiter(() =>
            withTimeout(
              overpassService.verifyPlace(poi.name, poi.location.coordinates, {
                proximity: anchor,
                maxDistanceMeters: maxDistanceKm * 1000
              }),
              VERIFICATION_TIMEOUT_MS,
              'Overpass verification',
              {
                matched: false,
                confidence: 0,
                source: 'overpass' as const,
                distanceMeters: undefined,
                name: undefined,
                location: undefined
              }
            )
          ).then(result => { verifications.overpass = result })
        )
      }

      await Promise.all(promises)
      return verifications
    })
  )

  return verificationPool
    .map((poi, idx) => {
      const verifications = allVerifications[idx]
      const { google, mapbox, overpass } = verifications
      
      const distanceKm = calculateDistance(anchor, poi.location.coordinates) / 1000
      const uniqueness = clampNumber(poi.uniqueness ?? 50, 0, 100)
      const aiConfidence = clampNumber(poi.confidence ?? 0, 0, 100)
      const aiRelevance = clampNumber(poi.relevance ?? 0, 0, 100)
      
      // Multi-source confidence scoring (adapt to available sources)
      const googleConfidence = clampNumber(google?.confidence ?? 0, 0, 100)
      const mapboxConfidence = clampNumber(mapbox?.confidence ?? 0, 0, 100)
      const overpassConfidence = clampNumber(overpass?.confidence ?? 0, 0, 100)
      
      // Count matching sources
      const matchCount = [
        google?.matched,
        mapbox?.matched,
        overpass?.matched
      ].filter(Boolean).length
      
      // Distance scoring
      const distanceScore = clampNumber(100 - (distanceKm / maxDistanceKm) * 80, 0, 100)
      
      // Adaptive weights based on available sources
      const sourceCount = verificationSources.length
      const baseWeight = 0.5 / Math.max(sourceCount, 1) // Split 50% among verification sources
      
      const verificationScore = (
        googleConfidence * (google ? baseWeight : 0) +
        overpassConfidence * (overpass ? baseWeight : 0) +
        mapboxConfidence * (mapbox ? baseWeight : 0) +
        aiConfidence * 0.25 +
        distanceScore * 0.1 +
        uniqueness * 0.15
      )
      
      // Bonus for agreement between sources
      let agreementBonus = 0
      if (matchCount >= 3) {
        agreementBonus = 20 // All sources agree - ultra confident
      } else if (matchCount === 2) {
        agreementBonus = 10 // 2 sources agree - very confident
      } else if (matchCount === 1) {
        agreementBonus = 3  // 1 source found it - somewhat confident
      }
      
      // Final confidence score
      const confidence = Math.min(100, Math.round(verificationScore + agreementBonus))
      
      // Relevance scoring
      const relevance = Math.round(
        confidence * 0.4 +
        uniqueness * 0.3 +
        distanceScore * 0.2 +
        (aiRelevance || 50) * 0.1
      )

      // Validate coordinates before returning
      const poiLat = poi.location.coordinates.lat
      const poiLng = poi.location.coordinates.lng
      
      // Reject 0,0 with tolerance (Gulf of Guinea bug)
      const isNearZeroZero = Math.abs(poiLat) < 0.01 && Math.abs(poiLng) < 0.01
      const isInvalid = !Number.isFinite(poiLat) || !Number.isFinite(poiLng) || Math.abs(poiLat) > 90 || Math.abs(poiLng) > 180
      
      if (isNearZeroZero || isInvalid) {
        logger.warn('Invalid POI coordinates detected - skipping', {
          name: poi.name,
          lat: poiLat,
          lng: poiLng,
          isNearZeroZero,
          isInvalid,
          category: poi.category,
          source: 'tier-verification'
        })
        return null as any
      }

      // Generate cover image URL if Google photo reference available
      let coverImage: string | undefined
      if (google?.photoReference) {
        coverImage = googlePlaces.getPlacePhotoUrl(google.photoReference, 600)
      }
      
      // Log valid POI coordinates for debugging
      logger.debug('Valid POI coordinates', {
        name: poi.name,
        lat: poiLat,
        lng: poiLng,
        category: poi.category
      })

      logger.debug('POI verified with tier sources', {
        name: poi.name,
        distanceKm: distanceKm.toFixed(2),
        confidence,
        relevance,
        matchCount,
        sources: verificationSources,
        googleMatched: google?.matched ?? null,
        overpassMatched: overpass?.matched ?? null,
        mapboxMatched: mapbox?.matched ?? null,
        coordinates: { lat: poiLat, lng: poiLng }
      })

      return {
        id: randomUUID(),
        name: poi.name,
        lat: poiLat,
        lng: poiLng,
        category: poi.category,
        description: poi.description,
        rating: poi.rating,
        uniqueness: uniqueness || 75,
        distanceKm,
        tags: poi.tags,
        relevance,
        confidence,
        coverImage,
        verification: {
          source: (google && google.matched) ? 'google-places' : 
                  (overpass && overpass.matched) ? 'overpass' : 
                  (mapbox && mapbox.matched) ? 'mapbox' : 'none',
          matched: matchCount > 0,
          confidence,
          distanceMeters: google?.distanceMeters ?? overpass?.distanceMeters ?? mapbox?.distanceMeters,
          name: google?.name ?? overpass?.name ?? mapbox?.name,
          coordinates: google?.location?.coordinates ?? overpass?.location?.coordinates ?? mapbox?.location?.coordinates,
          multiSource: {
            google: google ? {
              matched: google.matched,
              confidence: googleConfidence,
              placeId: (google as any).placeId
            } : undefined,
            mapbox: mapbox ? {
              matched: mapbox.matched,
              confidence: mapboxConfidence
            } : undefined,
            overpass: overpass ? {
              matched: overpass.matched,
              confidence: overpassConfidence,
              osmId: (overpass as any).osmId
            } : undefined,
            agreement: (() => {
              if (matchCount >= 3) return 'all'
              if (matchCount === 2) return 'majority'
              if (matchCount === 1) return 'partial'
              return 'none'
            })()
          }
        }
      }
    })
    .filter((poi): poi is NonNullable<typeof poi> => {
      // Filter out null POIs (invalid coordinates)
      if (!poi) return false
      
      // Dynamic confidence threshold based on search radius
      // Rural areas (>20km radius): lower threshold to 35 (rural places less indexed)
      // Suburban areas (10-20km): threshold 45
      // Urban areas (<10km): standard threshold 60
      const dynamicThreshold = getDynamicConfidenceThreshold(radiusKm)
      
      if (poi.confidence < dynamicThreshold) {
        logger.debug(`Filtering out low-confidence POI: ${poi.name} (confidence: ${poi.confidence}, min: ${dynamicThreshold}, radius: ${radiusKm}km)`)
        return false
      }
      return true
    })
    .sort((a, b) => {
      // Sort by relevance first, then confidence
      if ((b.relevance ?? 0) !== (a.relevance ?? 0)) {
        return (b.relevance ?? 0) - (a.relevance ?? 0)
      }
      return (b.confidence ?? 0) - (a.confidence ?? 0)
    })
}

/**
 * Legacy function - kept for backward compatibility
 * Verify POIs using multiple sources in parallel (Google Places + Mapbox + Overpass)
 * and calculate comprehensive confidence scores
 * 
 * Multi-source verification provides:
 * - Higher confidence when multiple sources agree
 * - Cross-validation of coordinates
 * - Redundancy if one service fails
 * - Better place name matching
 */
async function verifyAndScorePois(
  pois: Array<{
    name: string
    description: string
    location: { coordinates: { lat: number; lng: number } }
    category: string
    tags: string[]
    uniqueness: number
    rating?: number
    confidence?: number
    relevance?: number
  }>,
  anchor: { lat: number; lng: number },
  radiusKm: number
) {
  const effectiveRadiusKm = Math.max(radiusKm, 1)
  // CRITICAL FIX: Use strict radius (no buffer beyond user's request)
  const maxDistanceKm = effectiveRadiusKm

  const uniquePois = dedupePois(pois)
  
  // CRITICAL FIX: Pre-filter by strict distance BEFORE verification to save API calls
  const filteredPois = uniquePois.filter(poi => {
    const distanceKm = calculateDistance(anchor, poi.location.coordinates) / 1000
    if (distanceKm > maxDistanceKm) {
      logger.debug(`Rejecting POI "${poi.name}" - too far (${distanceKm.toFixed(1)}km > ${maxDistanceKm}km)`)
      return false
    }
    return true
  })

  const verificationPool = rankAndLimitPois(filteredPois, anchor, MAX_VERIFICATION_CANDIDATES)

  logger.info('Verifying POIs with multi-source verification', {
    total: pois.length,
    unique: uniquePois.length,
    withinRadius: filteredPois.length,
    verificationPool: verificationPool.length,
    limit: MAX_VERIFICATION_CANDIDATES,
    radiusKm: maxDistanceKm
  })

  // Verify each POI with Google Places, Mapbox AND Overpass in parallel
  const allVerifications = await Promise.all(
    verificationPool.map(async (poi) => {
      const [googleResult, mapboxResult, overpassResult] = await Promise.all([
        withTimeout(
          googlePlaces.verifyPlace(poi.name, poi.location.coordinates, {
            proximity: anchor,
            maxDistanceMeters: maxDistanceKm * 1000
          }),
          VERIFICATION_TIMEOUT_MS,
          'Google verification',
          { 
            matched: false, 
            confidence: 0, 
            source: 'google-places' as const,
            distanceMeters: undefined,
            name: undefined,
            location: undefined
          }
        ),
        withTimeout(
          mapboxPlaces.verifyPlace(poi.name, poi.location.coordinates, {
            proximity: anchor,
            maxDistanceMeters: maxDistanceKm * 1000
          }),
          VERIFICATION_TIMEOUT_MS,
          'Mapbox verification',
          { 
            matched: false, 
            confidence: 0, 
            source: 'mapbox' as const,
            distanceMeters: undefined,
            name: undefined,
            location: undefined
          }
        ),
        withTimeout(
          overpassService.verifyPlace(poi.name, poi.location.coordinates, {
            proximity: anchor,
            maxDistanceMeters: maxDistanceKm * 1000
          }),
          VERIFICATION_TIMEOUT_MS,
          'Overpass verification',
          { 
            matched: false, 
            confidence: 0, 
            source: 'overpass' as const,
            distanceMeters: undefined,
            name: undefined,
            location: undefined
          }
        )
      ])

      return { google: googleResult, mapbox: mapboxResult, overpass: overpassResult }
    })
  )

  return verificationPool
    .map((poi, idx) => {
      const { google, mapbox, overpass } = allVerifications[idx]
      const distanceKm = calculateDistance(anchor, poi.location.coordinates) / 1000
      const uniqueness = clampNumber(poi.uniqueness ?? 50, 0, 100)
      const aiConfidence = clampNumber(poi.confidence ?? 0, 0, 100)
      const aiRelevance = clampNumber(poi.relevance ?? 0, 0, 100)
      
      // Multi-source confidence scoring (3 sources now)
      const googleConfidence = clampNumber(google.confidence ?? 0, 0, 100)
      const mapboxConfidence = clampNumber(mapbox.confidence ?? 0, 0, 100)
      const overpassConfidence = clampNumber(overpass.confidence ?? 0, 0, 100)
      
      // Count matching sources
      const matchCount = [google.matched, mapbox.matched, overpass.matched].filter(Boolean).length
      
      // Distance scoring (more generous)
      const distanceScore = clampNumber(100 - (distanceKm / maxDistanceKm) * 80, 0, 100)
      
      // Source reliability weights (3 sources: Google, Overpass, Mapbox, AI)
      const googleWeight = 0.25   // Google Places (paid, reliable)
      const overpassWeight = 0.25 // Overpass/OSM (free, good for nature/heritage)
      const mapboxWeight = 0.15   // Mapbox (backup)
      const aiWeight = 0.25       // AI confidence
      const distanceWeight = 0.1  // Distance factor
      
      // Calculate verification confidence (weighted average of sources)
      const verificationScore = (
        googleConfidence * googleWeight +
        overpassConfidence * overpassWeight +
        mapboxConfidence * mapboxWeight +
        aiConfidence * aiWeight +
        distanceScore * distanceWeight
      )
      
      // Bonus for agreement between sources
      let agreementBonus = 0
      if (matchCount === 3) {
        agreementBonus = 20 // All 3 sources agree - ultra confident
      } else if (matchCount === 2) {
        agreementBonus = 10 // 2 sources agree - very confident
      } else if (matchCount === 1) {
        agreementBonus = 3  // 1 source found it - somewhat confident
      }
      
      // Final confidence score
      const confidence = Math.min(100, Math.round(verificationScore + agreementBonus))
      
      // Relevance scoring (combines verification, uniqueness, distance)
      const relevance = Math.round(
        confidence * 0.4 +           // Verified places are more relevant
        uniqueness * 0.3 +           // Unique places are interesting
        distanceScore * 0.2 +        // Closer is better
        (aiRelevance || 50) * 0.1    // AI's opinion matters a bit
      )

      // Choose best verification result for metadata
      const verifications = [
        { result: google, confidence: googleConfidence },
        { result: overpass, confidence: overpassConfidence },
        { result: mapbox, confidence: mapboxConfidence }
      ].sort((a, b) => b.confidence - a.confidence)
      
      const bestVerification = verifications[0].result
      const secondVerification = verifications[1].result

      // Validate coordinates before returning
      const poiLat = poi.location.coordinates.lat
      const poiLng = poi.location.coordinates.lng
      
      // Reject 0,0 with tolerance (Gulf of Guinea bug)
      const isNearZeroZero = Math.abs(poiLat) < 0.01 && Math.abs(poiLng) < 0.01
      const isInvalid = !Number.isFinite(poiLat) || !Number.isFinite(poiLng) || Math.abs(poiLat) > 90 || Math.abs(poiLng) > 180
      
      if (isNearZeroZero || isInvalid) {
        logger.warn('Invalid POI coordinates detected - skipping (legacy)', {
          name: poi.name,
          lat: poiLat,
          lng: poiLng,
          isNearZeroZero,
          isInvalid,
          category: poi.category,
          source: 'legacy-verification'
        })
        return null as any
      }
      
      // Log valid POI coordinates for debugging
      logger.debug('Valid POI coordinates (legacy)', {
        name: poi.name,
        lat: poiLat,
        lng: poiLng,
        category: poi.category
      })

      logger.debug('POI verified', {
        name: poi.name,
        distanceKm: distanceKm.toFixed(2),
        confidence,
        relevance,
        matchCount,
        googleMatched: google.matched,
        overpassMatched: overpass.matched,
        mapboxMatched: mapbox.matched,
        coordinates: { lat: poiLat, lng: poiLng }
      })

      return {
        id: randomUUID(),
        name: poi.name,
        lat: poiLat,
        lng: poiLng,
        category: poi.category,
        description: poi.description,
        rating: poi.rating,
        uniqueness: uniqueness || 75,
        distanceKm,
        tags: poi.tags,
        relevance,
        confidence,
        verification: {
          // Primary verification source (highest confidence)
          source: bestVerification.source,
          matched: bestVerification.matched || secondVerification.matched,
          confidence: Math.max(bestVerification.confidence ?? 0, secondVerification.confidence ?? 0),
          distanceMeters: bestVerification.distanceMeters ?? secondVerification.distanceMeters,
          name: bestVerification.name ?? secondVerification.name,
          coordinates: bestVerification.location?.coordinates ?? secondVerification.location?.coordinates,
          // Multi-source verification metadata (3 sources now)
          multiSource: {
            google: {
              matched: google.matched,
              confidence: googleConfidence,
              placeId: (google as any).placeId
            },
            mapbox: {
              matched: mapbox.matched,
              confidence: mapboxConfidence
            },
            overpass: {
              matched: overpass.matched,
              confidence: overpassConfidence,
              osmId: (overpass as any).osmId
            },
            agreement: (() => {
              if (matchCount === 3) return 'all'
              if (matchCount === 2) return 'majority'
              if (matchCount === 1) return 'partial'
              return 'none'
            })()
          }
        }
      }
    })
      // STRICTER FILTER: Only keep POIs with reasonable confidence
    .filter((poi): poi is NonNullable<typeof poi> => {
      // Filter out null POIs (invalid coordinates)
      if (!poi) return false
      
      // Require at least configured confidence (was 40)
      if (poi.confidence < MIN_POI_CONFIDENCE) {
        logger.debug(`Filtering out low-confidence POI: ${poi.name} (confidence: ${poi.confidence}, min: ${MIN_POI_CONFIDENCE})`)
        return false
      }
      return true
    })
    .sort((a, b) => {
      // Sort by relevance first (verified + unique + close)
      if ((b.relevance ?? 0) !== (a.relevance ?? 0)) {
        return (b.relevance ?? 0) - (a.relevance ?? 0)
      }
      // Then by confidence (verification certainty)
      if ((b.confidence ?? 0) !== (a.confidence ?? 0)) {
        return (b.confidence ?? 0) - (a.confidence ?? 0)
      }
      // Finally by distance (nearest first)
      return (a.distanceKm ?? 999) - (b.distanceKm ?? 999)
    })
}

export async function processPoiSuggestion(payload: POIJobPayload): Promise<POIProcessingResult> {
  // Initialize POI database lazily (only when first needed)
  if (ENABLE_POI_DATABASE) {
    await initPOIDatabase()
  }
  
  const { location, categories, radiusKm, preferences, cacheKey, userId, userTier, planId, stageId, planName, stageName } = payload
  const startTime = Date.now()
  const validation = JOB_INPUT_SCHEMA.safeParse({
    location,
    categories,
    radiusKm,
    preferences,
    context: payload.context,
    planId,
    stageId,
    planName,
    stageName
  })

  if (!validation.success) {
    const processingTimeMs = Date.now() - startTime
    const metadata = {
      ...buildMetadata('validation-error', processingTimeMs, radiusKm, categories, false, {
        reason: 'invalid_payload',
        rawError: validation.error.message
      }),
      verifiedCount: 0,
      inputCount: 0,
      tier: userTier
    }

    logger.warn('POI suggestion payload failed validation', {
      cacheKey,
      issues: validation.error.issues
    })

    return {
      success: false,
      pois: [],
      source: 'validation-error',
      metadata,
      planId,
      stageId,
      planName,
      stageName
    }
  }

  // Check cache first (huge performance win!)
  if (cacheKey) {
    try {
      const cached = await repository.getCachedResult(cacheKey) as {
        pois?: unknown[]
        metadata?: Record<string, unknown>
      } | null
      
      if (cached?.pois && Array.isArray(cached.pois)) {
        logger.info('Cache HIT - returning cached POIs', { 
          cacheKey,
          poisCount: cached.pois.length,
          cacheAge: cached.metadata?.generatedAt 
        })
        
        return {
          success: true,
          pois: cached.pois,
          source: (cached.metadata?.source as string) || 'cache',
          metadata: {
            ...cached.metadata,
            cached: true,
            processingTimeMs: Date.now() - startTime // Cache retrieval time
          },
          planId,
          stageId,
          planName,
          stageName
        }
      }
    } catch (cacheError) {
      logger.warn('Cache check failed, proceeding with generation', { cacheError })
    }
  }

  // Tier-based source selection
  const tier = userTier || 'standard'
  const aiTier: AIModelTier = (tier === 'free' ? 'standard' : tier) as AIModelTier
  const { suggestionSources, verificationSources } = selectSourcesForTier(tier)
  
  logger.info('Cache MISS - checking POI database first', { 
    cacheKey, 
    location, 
    categories, 
    radiusKm,
    tier,
    databaseEnabled: ENABLE_POI_DATABASE,
    minDatabaseResults: MIN_DATABASE_RESULTS,
    minDatabaseConfidence: MIN_POI_CONFIDENCE
  })

  // STEP 1: Check POI database (permanent, user-enriched)
  let databasePOIs: Array<{
    id?: string
    name: string
    lat: number
    lng: number
    category: string
    description?: string
    rating?: number
    uniqueness?: number
    tags?: string[]
    confidence?: number
    coverImage?: string
    names?: {
      native: string
      local: string
      english: string | null
      international: string
      translations: Record<string, string>
      alt: string | null
    }
    verification?: any
    distanceKm?: number
  }> = []

  if (ENABLE_POI_DATABASE) {
    const databaseSearchStart = Date.now()
    databasePOIs = await poiDatabase?.searchNearby(
      location,
      categories,
      radiusKm,
      { minConfidence: MIN_POI_CONFIDENCE, limit: 50 }
    ) ?? []
    const databaseSearchMs = Date.now() - databaseSearchStart

    logger.info('POI database search complete', {
      found: databasePOIs.length,
      timeMs: databaseSearchMs,
      categories
    })
  } else {
    logger.info('POI database disabled via ENABLE_POI_DATABASE=false - skipping lookup')
  }

  // If database has enough POIs, use them (no AI needed!)
  if (databasePOIs.length >= MIN_DATABASE_RESULTS) {
    logger.info('✅ Database has sufficient POIs - skipping AI generation', {
      count: databasePOIs.length,
      saved: 'AI call + verification',
      minDatabaseResults: MIN_DATABASE_RESULTS
    })

    // Cache the database results for future queries
    const databaseMetadata = {
      source: 'database',
      processingTimeMs: Date.now() - startTime,
      cached: false,
      databaseOnly: true,
      databaseResults: databasePOIs.length
    }

    if (cacheKey) {
      await repository.cacheResult(cacheKey, {
        pois: databasePOIs,
        metadata: databaseMetadata
      }).catch(err => logger.warn('Failed to cache database results', { err }))
    }

    return {
      success: true,
      pois: databasePOIs,
      source: 'database',
      metadata: databaseMetadata,
      planId,
      stageId,
      planName,
      stageName
    }
  }

  // STEP 2: Database doesn't have enough - generate with AI (and save to database)
    logger.info('Database insufficient - generating with AI', {
      databaseResults: databasePOIs.length,
      needed: MIN_DATABASE_RESULTS,
      suggestionSources,
      verificationSources
    })

  // Attempt to get suggestions from configured sources IN PARALLEL
  let rawPois: Array<{
    name: string
    description: string
    location: { coordinates: { lat: number; lng: number } }
    category: string
    tags: string[]
    uniqueness: number
    rating?: number
    confidence?: number
    relevance?: number
    coverImage?: string
  }> = []
  let primarySource = 'unknown'
  const sourceErrors: Record<string, string> = {}

  const runSource = (source: 'overpass' | 'openai' | 'mapbox') => {
    if (source === 'openai') {
      return (async () => {
        const aiCallStart = Date.now()
        try {
          const aiPois = await withTimeout(
                openai.suggestPOIs(
                  location, 
                  categories, 
                  radiusKm, 
                  preferences, 
                  aiTier, 
                  undefined, 
                  { userId, feature: 'ai:suggest-pois' }
                ),
            SUGGESTION_TIMEOUT_MS,
            'OpenAI suggestions',
            []
          )
          const aiCallMs = Date.now() - aiCallStart
          logger.info('OpenAI suggestions retrieved', { count: aiPois.length, timeMs: aiCallMs })
          return { source: 'openai', pois: aiPois }
        } catch (error) {
          logger.warn('OpenAI suggestion failed', { error, tier })
          sourceErrors.openai = String(error)
          return { source: 'openai', pois: [] }
        }
      })()
    }

    if (source === 'overpass') {
      return (async () => {
        const overpassStart = Date.now()
        try {
          const overpassPois = await withTimeout(
            getSuggestionsFromOverpass(location, categories, radiusKm),
            SUGGESTION_TIMEOUT_MS,
            'Overpass suggestions',
            []
          )
          const overpassMs = Date.now() - overpassStart
          logger.info('Overpass suggestions retrieved', { count: overpassPois.length, timeMs: overpassMs })
          return { source: 'overpass', pois: overpassPois }
        } catch (error) {
          logger.warn('Overpass suggestion failed', { error, tier })
          sourceErrors.overpass = String(error)
          return { source: 'overpass', pois: [] }
        }
      })()
    }

    // mapbox
    return (async () => {
      const mapboxStart = Date.now()
      try {
        const uniqueCategories = Array.from(new Set(categories)).slice(0, MAX_CATEGORY_REQUESTS)
        if (uniqueCategories.length < categories.length) {
          logger.info('Mapbox categories truncated', {
            requested: categories.length,
            used: uniqueCategories.length,
            limit: MAX_CATEGORY_REQUESTS
          })
        }

        const mapboxLimiter = createLimiter(MAX_SUGGESTION_CONCURRENCY)

        // Parallelize category searches with bounded concurrency
        const categoryPromises = uniqueCategories.map(category =>
          mapboxLimiter(() =>
            mapboxPlaces.searchNearby(location, category, radiusKm * 1000)
          ).then(results =>
            results.filter(r => {
              const lat = r.location.coordinates.lat
              const lng = r.location.coordinates.lng
              const isNearZeroZero = Math.abs(lat) < 0.01 && Math.abs(lng) < 0.01
              
              if (isNearZeroZero) {
                logger.warn('Mapbox returned 0,0 coordinates - filtering', {
                  name: r.name,
                  lat,
                  lng,
                  category: r.category
                })
                return false
              }
              return true
            })
          )
        )
        
        const allResults = await withTimeout(
          Promise.all(categoryPromises),
          SUGGESTION_TIMEOUT_MS,
          'Mapbox suggestions',
          [] as Array<Awaited<ReturnType<typeof mapboxPlaces.searchNearby>>>
        )
        const mapboxPois = allResults.flat().map(r => ({
          name: r.name,
          description: r.description,
          location: r.location,
          category: r.category,
          tags: [],
          uniqueness: 50,
          rating: r.rating
        }))
        
        const mapboxMs = Date.now() - mapboxStart
        logger.info('Mapbox suggestions retrieved', { count: mapboxPois.length, timeMs: mapboxMs })
        return { source: 'mapbox', pois: mapboxPois }
      } catch (error) {
        logger.warn('Mapbox suggestion failed', { error, tier })
        sourceErrors.mapbox = String(error)
        return { source: 'mapbox', pois: [] }
      }
    })()
  }

  const consumeResults = (results: Array<Promise<{ source: string; pois: typeof rawPois }>>) =>
    Promise.all(results).then(resolved => {
      for (const result of resolved) {
        if (result.pois.length > 0) {
          rawPois.push(...result.pois)
          if (!primarySource || primarySource === 'unknown') {
            primarySource = result.source
          }
        }
      }
    })

  const lowCostSources = suggestionSources.filter(source => source !== 'openai')
  const aiSources = suggestionSources.filter(source => source === 'openai')

  // Run low-cost sources first (parallelized)
  if (lowCostSources.length > 0) {
    await consumeResults(lowCostSources.map(source => runSource(source)))
  }

  // Only call expensive sources (OpenAI) if we still need more POIs
  if (aiSources.length > 0 && rawPois.length < MIN_RESULTS_BEFORE_SKIPPING_AI) {
    await consumeResults(aiSources.map(source => runSource(source)))
  } else if (aiSources.length > 0) {
    logger.info('Skipping OpenAI suggestions - sufficient POIs from low-cost sources', {
      collected: rawPois.length,
      threshold: MIN_RESULTS_BEFORE_SKIPPING_AI,
      sourcesUsed: lowCostSources
    })
  }
  // Absolute fallback: if nothing gathered, try OpenAI once (except free tier)
  if (rawPois.length === 0 && userTier !== 'free' && !aiSources.includes('openai')) {
    logger.info('No POIs from configured sources - invoking OpenAI fallback')
    await consumeResults([runSource('openai')])
  }

  const metadataSource = primarySource === 'mapbox' ? 'mapbox-places' : (primarySource || 'none')

  // If no suggestions found from any source
  if (rawPois.length === 0) {
    const totalMs = Date.now() - startTime
    const diagnostic = {
      reason: 'all_sources_failed',
      aiReturnedCount: 0,
      afterDedup: 0,
      withinRadius: 0,
      afterVerification: 0,
      suggestions: [
        `Aucune suggestion trouvée (tier: ${tier})`,
        `Sources tentées: ${suggestionSources.join(', ')}`,
        'Essayez d\'augmenter le rayon de recherche',
        'Vérifiez la zone géographique'
      ],
      timings: { totalMs },
      services: Object.fromEntries(
        suggestionSources.map(s => [s, { success: false, error: sourceErrors[s] || 'No results' }])
      ),
      tier,
      cacheHit: false,
      coordinates: location
    }

    const metadata = {
      ...buildMetadata(metadataSource || 'none', totalMs, radiusKm, categories, false, diagnostic),
      verifiedCount: 0,
      inputCount: rawPois.length,
      tier,
      suggestionSources,
      verificationSources
    }
    
    logger.warn('No suggestions from any source', { tier, suggestionSources, sourceErrors })
    
    return { 
      success: false, 
      pois: [], 
      source: primarySource || 'none', 
      metadata, 
      planId, 
      stageId, 
      planName, 
      stageName 
    }
  }

  // Deduplicate combined suggestions
  const uniquePois = dedupePois(rawPois)
  const withinRadiusPois = uniquePois.filter(poi => {
    const distanceKm = calculateDistance(location, poi.location.coordinates) / 1000
    const isWithin = distanceKm <= Math.max(radiusKm, 1)
    
    // Debug: Log distance calculations for first few POIs
    if (!isWithin) {
      logger.debug('POI outside radius (pre-verification filter)', {
        name: poi.name,
        distanceKm: distanceKm.toFixed(2),
        radiusKm,
        searchCenter: location,
        poiCoords: poi.location.coordinates
      })
    }
    
    return isWithin
  })

  logger.info('Combined suggestions from sources', {
    rawCount: rawPois.length,
    afterDedup: uniquePois.length,
    withinRadius: withinRadiusPois.length,
    sources: suggestionSources
  })

  const quickResponseEnabled =
    process.env.NODE_ENV !== 'test' &&
    process.env.ENABLE_QUICK_RESPONSE !== 'false'
  const quickPool = rankAndLimitPois(
    withinRadiusPois,
    location,
    Math.min(MAX_QUICK_RESULTS, MAX_VERIFICATION_CANDIDATES)
  )

  if (quickResponseEnabled) {
    const quickPois = quickPool
      .map(poi => formatQuickPoi(poi, location, radiusKm))
      .filter((poi): poi is NonNullable<ReturnType<typeof formatQuickPoi>> => Boolean(poi))
    const dynamicThreshold = getDynamicConfidenceThreshold(radiusKm)
    const filteredQuickPois = quickPois.filter(poi => (poi.confidence ?? 0) >= dynamicThreshold)

    const quickProcessingMs = Date.now() - startTime
    const quickMetadata = {
      ...buildMetadata(metadataSource, quickProcessingMs, radiusKm, categories, false),
      verifiedCount: 0,
      inputCount: rawPois.length,
      tier,
      suggestionSources,
      verificationSources,
      quickResponse: true,
      verified: false
    }

    if (cacheKey) {
      repository
        .cacheResult(cacheKey, { pois: filteredQuickPois, metadata: quickMetadata }, 900)
        .catch(err => logger.warn('Failed to cache quick-response POIs', { err, cacheKey }))
    }

    // Background verification with bounded concurrency
    void (async () => {
      try {
        const verificationStart = Date.now()
        const verifiedPois = await verifyAndScorePoisWithTier(
          quickPool,
          location,
          radiusKm,
          verificationSources
        )
        const verificationMs = Date.now() - verificationStart
        const metadata = {
          ...buildMetadata(metadataSource, Date.now() - startTime, radiusKm, categories, false),
          verifiedCount: verifiedPois.length,
          inputCount: rawPois.length,
          tier,
          suggestionSources,
          verificationSources,
          quickResponse: true,
          verified: true,
          verificationMs
        }

        if (cacheKey) {
          await repository.cacheResult(cacheKey, { pois: verifiedPois, metadata }, 3600)
        }

        await repository.saveSuggestion({
          type: 'pois',
          input: { location, categories, radiusKm, preferences, tier },
          output: verifiedPois,
          model: metadata.model ?? metadata.source,
          userId,
          timestamp: new Date(),
          metadata
        })
      } catch (error) {
        logger.error('Background verification failed', { error, cacheKey })
      }
    })()

    return {
      success: filteredQuickPois.length > 0,
      pois: filteredQuickPois,
      source: primarySource,
      metadata: quickMetadata,
      planId,
      stageId,
      planName,
      stageName
    }
  }

  // Verify with tier-appropriate sources
  const verificationStart = Date.now()
  let pois = await verifyAndScorePoisWithTier(
    quickPool, 
    location, 
    radiusKm, 
    verificationSources
  )
  const verificationMs = Date.now() - verificationStart
  let usedUnverifiedFallback = false

  // Fallback: if verification returns 0 results but quick candidates exist, return them unverified
  if (!pois.length && quickPool.length > 0) {
    const quickFallback = quickPool
      .map(poi => formatQuickPoi(poi, location, radiusKm))
      .filter((poi): poi is NonNullable<ReturnType<typeof formatQuickPoi>> => Boolean(poi))

    const dynamicThreshold = getDynamicConfidenceThreshold(radiusKm)
    const filteredFallback = quickFallback.filter(poi => (poi.confidence ?? 0) >= dynamicThreshold)

    if (filteredFallback.length > 0) {
      usedUnverifiedFallback = true
      pois = filteredFallback
      logger.warn('Verification returned 0 POIs - using unverified quick fallback', {
        quickCandidates: quickFallback.length,
        returned: filteredFallback.length,
        radiusKm,
        dynamicThreshold
      })
    }
  }
  
  // Build diagnostic info for empty results
  let diagnostic: {
    reason: string
    aiReturnedCount: number
    afterDedup: number
    withinRadius: number
    afterVerification: number
    suggestions: string[]
    timings?: { totalMs: number; verificationMs?: number }
    services?: Record<string, { success: boolean; verified?: number; error?: string }>
    filters?: {
      duplicatesRemoved: number
      outsideRadius: number
      failedVerification: number
    }
    tier?: string
    cacheHit?: boolean
    coordinates?: { lat: number; lng: number }
  } | undefined

  if (pois.length === 0) {
    let reason: string
    let suggestions: string[]
    
    if (rawPois.length === 0) {
      reason = 'no_suggestions_found'
      suggestions = [
        `Aucune suggestion trouvée (tier: ${tier})`,
        'Essayez d\'augmenter le rayon de recherche',
        'Modifiez les catégories de POI',
        'Vérifiez que la zone géographique contient des points d\'intérêt'
      ]
    } else if (quickPool.length === 0) {
      reason = 'all_outside_radius'
      suggestions = [
        'Augmentez le rayon de recherche',
        `Rayon actuel: ${radiusKm}km - tous les POI trouvés sont en dehors`
      ]
    } else {
      reason = 'all_failed_verification'
      suggestions = [
        'Les POI trouvés n\'ont pas pu être vérifiés',
        `Sources de vérification: ${verificationSources.join(', ')}`,
        'Essayez une zone plus populaire ou touristique',
        'Augmentez le rayon pour élargir la recherche'
      ]
    }
    
    const totalMs = Date.now() - startTime
    diagnostic = {
      reason,
      aiReturnedCount: rawPois.length,
      afterDedup: uniquePois.length,
      withinRadius: quickPool.length,
      afterVerification: 0,
      suggestions,
      timings: {
        totalMs,
        verificationMs
      },
      services: Object.fromEntries(
        [...suggestionSources, ...verificationSources].map(s => [
          s, 
          { success: !sourceErrors[s], error: sourceErrors[s], verified: 0 }
        ])
      ),
      filters: {
        duplicatesRemoved: rawPois.length - uniquePois.length,
        outsideRadius: uniquePois.length - quickPool.length,
        failedVerification: quickPool.length
      },
      tier,
      cacheHit: false,
      coordinates: location
    }
  }

  if (!pois.length) {
    const processingTimeMs = Date.now() - startTime
    const metadata = {
      ...buildMetadata(
        metadataSource,
        processingTimeMs,
        radiusKm,
        categories,
        false,
        diagnostic
      ),
      verifiedCount: 0,
      inputCount: rawPois.length,
      tier
    }
    
    logger.info('No POIs passed verification (tier-based)', { 
      diagnostic,
      tier,
      planId, 
      stageId 
    })
    
    return { success: false, pois: [], source: primarySource, metadata, planId, stageId, planName, stageName }
  }

  // STEP 3: Save all verified POIs to permanent database
  // This enriches the collective knowledge base for future users
  if (pois.length > 0 && ENABLE_POI_DATABASE && !usedUnverifiedFallback) {
    logger.info('💾 Saving POIs to permanent database', { count: pois.length })
    
    void poiDatabase?.savePOIs?.(
      pois.map(poi => ({
        name: poi.name,
        lat: poi.lat,
        lng: poi.lng,
        category: poi.category,
        description: poi.description,
        rating: poi.rating,
        uniqueness: poi.uniqueness,
        tags: poi.tags,
        confidence: poi.confidence,
        coverImage: poi.coverImage,
        verified: poi.verified,
        source: poi.source,
        metadata: { userId, tier, generatedAt: new Date() }
      }))
    )?.catch(err => logger.warn('Failed to save POIs to database', { err, count: pois.length }))
    // Fire & forget - don't block response
  } else if (pois.length > 0 && (!ENABLE_POI_DATABASE || usedUnverifiedFallback)) {
    logger.info('POI database save skipped', { count: pois.length, usedUnverifiedFallback, databaseEnabled: ENABLE_POI_DATABASE })
  }

  // Standard mode (sync verification)
  const processingTimeMs = Date.now() - startTime
  const verifiedCount = usedUnverifiedFallback ? 0 : pois.length
  const metadata = {
    ...buildMetadata(
      metadataSource,
      processingTimeMs,
      radiusKm,
      categories,
      false
    ),
    verifiedCount,
    inputCount: rawPois.length,
    tier,
    suggestionSources,
    verificationSources,
    verified: !usedUnverifiedFallback,
    verificationFailed: usedUnverifiedFallback,
    quickFallback: usedUnverifiedFallback
  }

  if (cacheKey) {
    await repository.cacheResult(cacheKey, { pois, metadata }, 3600)
  }

  await repository.saveSuggestion({
    type: 'pois',
    input: { location, categories, radiusKm, preferences, tier },
    output: pois,
    model: metadata.model ?? metadata.source,
    userId,
    timestamp: new Date(),
    metadata
  })

  logger.info('Generated POI suggestions with tier-based strategy', { 
    poisCount: pois.length, 
    processingTimeMs, 
    tier,
    suggestionSources,
    verificationSources,
    planId, 
    stageId 
  })

  return { success: true, pois, source: primarySource, metadata, planId, stageId, planName, stageName }
}
function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  label: string,
  fallback: T
): Promise<T> {
  let timer: NodeJS.Timeout | undefined
  let resolved = false

  const timeoutPromise = new Promise<T>((resolve) => {
    timer = setTimeout(() => {
      resolved = true
      logger.warn(`${label} timed out`, { timeoutMs })
      resolve(fallback)
    }, timeoutMs)
  })

  return Promise.race([promise, timeoutPromise])
    .catch((error: unknown) => {
      if (!resolved) {
        logger.warn(`${label} failed`, { error })
      }
      return fallback
    })
    .finally(() => {
      if (timer) clearTimeout(timer)
    })
}

function rankAndLimitPois<T extends { location: { coordinates: { lat: number; lng: number } }; relevance?: number; uniqueness?: number; rating?: number }>(
  pois: T[],
  anchor: { lat: number; lng: number },
  limit = MAX_VERIFICATION_CANDIDATES
): T[] {
  if (pois.length <= limit) return pois

  return pois
    .map(poi => {
      const distanceKm = calculateDistance(anchor, poi.location.coordinates) / 1000
      const relevance = poi.relevance ?? 0
      const uniqueness = poi.uniqueness ?? 0
      const rating = poi.rating ?? 0

      return {
        poi,
        distanceKm,
        score: (100 - distanceKm) * 0.5 + relevance * 0.2 + uniqueness * 0.2 + rating * 0.1
      }
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(entry => entry.poi)
}
